import React, { useEffect, useState, useRef, useCallback } from "react";
import { Form, useFormik, FormikProvider, Field } from "formik";
import { useAuth } from "../../../context/AuthContext";
import ProductDisplay from "./ProductDisplay";
import AddressForm from "./AddressForm";
import { toast } from "react-toastify";
import * as Yup from 'yup';
import { addressValidationSchema } from '../../../utils/addressValidation';
import { useCapsuleOrder } from "./CapsuleOrderContext";
import { useNavigate, useOutletContext } from 'react-router-dom';
import Modal from "../../Common/Modal/Modal";
import LoadingSpinner from "../../Common/Loading/LoadingSpinner";
import {
    ShoppingBagIcon,
    ShoppingCartIcon,
    UsersIcon,
    UserGroupIcon,
    CheckCircleIcon,
    InformationCircleIcon,
    UserIcon,
} from '@heroicons/react/24/outline';
import Flag from "react-flagkit";
import { getCountryByIso } from "../../../utils/country.utils";
import SuccessMessage from "./SuccessMessage";
import ErrorMessage from "./ErrorMessage";
import { Loader2, MapPin, Store } from "lucide-react";
import { buildOrderPayload } from "../../../utils/addressUtils";
import UnifiedPhoneInput from "../../Common/Inputs/UnifiedPhoneInput/UnifiedPhoneInput";

const validationFormSchema = Yup.object().shape({
    shippingAddressId: Yup.number().integer().nullable(),
    shipping: Yup.object().shape({
        address_1: Yup.string().required('Adresse est requise'),
        city: Yup.string().required('Ville est requise'),
        postcode: Yup.string().required('Code Postal est requis'),
        state: Yup.string().default(''),
        country: Yup.string().required('Pays est requis'),
        first_name: Yup.string().required('Prénom est requis'),
        last_name: Yup.string().required('Nom est requis'),
        legal_first_name: Yup.string().when('$useLegalRepresentative', {
            is: true,
            then: (schema) => schema.required('Prénom du représentant légal est requis'),
            otherwise: (schema) => schema.optional(),
        }),
        legal_last_name: Yup.string().when('$useLegalRepresentative', {
            is: true,
            then: (schema) => schema.required('Nom du représentant légal est requis'),
            otherwise: (schema) => schema.optional(),
        }),
    })
});

const CapsuleOrder = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const { capsules, therapist, loading, error, product, createOrder } = useCapsuleOrder();
    const [shippingType, setShippingType] = useState('customer');
    const [useLegalRepresentative, setUseLegalRepresentative] = useState(false);
    const { customer } = useOutletContext();
    const modalRef = useRef(null);
    const [pendingValues, setPendingValues] = useState(null);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isCreating, setIsCreating] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [hasNoAddress, setHasNoAddress] = useState(false);

    // Fonction utilitaire pour séparer le préfixe du numéro de téléphone
    const separatePhoneAndPrefix = useCallback((fullPhone, userPrefix) => {
        if (!fullPhone) return { phone: '', prefix: userPrefix || '+33' };

        // Si le numéro commence par +, on extrait le préfixe
        if (fullPhone.startsWith('+')) {
            // Chercher le préfixe le plus long qui correspond
            const prefixes = ['+34', '+33', '+1', '+44', '+49', '+39', '+41', '+32', '+31']; // Ajouter d'autres préfixes si nécessaire
            const matchingPrefix = prefixes.find(prefix => fullPhone.startsWith(prefix));

            if (matchingPrefix) {
                return {
                    phone: fullPhone.substring(matchingPrefix.length),
                    prefix: matchingPrefix
                };
            }
        }

        // Si pas de préfixe détecté, utiliser le préfixe de l'utilisateur ou défaut
        return {
            phone: fullPhone,
            prefix: userPrefix || '+33'
        };
    }, []);


    useEffect(() => {
        if (therapist) {
            if (therapist.addresses && therapist.addresses.length === 0) {
                setHasNoAddress(true);
            } else {
                setHasNoAddress(false);
            }
        }

    }, [therapist])

    const formik = useFormik({
        initialValues: {
            shippingAddressId: null,
            shipping: {
                address_1: "",
                city: "",
                postcode: "",
                state: "",
                country: "",
                first_name: "",
                last_name: "",
                legal_first_name: "",
                legal_last_name: "",
            },
            billing: {
                phone: "",
                phonePrefix: ""
            }
        },
        validationSchema: validationFormSchema,
        context: { useLegalRepresentative },
        validate: (values) => {
            const errors = {};

            // Custom validation for phone when shipping to customer
            if (shippingType === 'customer' && (!values.billing?.phone || values.billing.phone.trim() === '')) {
                if (!errors.billing) errors.billing = {};
                errors.billing.phone = 'Téléphone est requis pour un envoi au client';
            }

            // Custom validation for legal representative fields
            if (useLegalRepresentative) {


                if (!values.shipping.legal_first_name || values.shipping.legal_first_name.trim() === '') {
                    if (!errors.shipping) errors.shipping = {};
                    errors.shipping.legal_first_name = 'Prénom du représentant légal est requis';
                }

                if (!values.shipping.legal_last_name || values.shipping.legal_last_name.trim() === '') {
                    if (!errors.shipping) errors.shipping = {};
                    errors.shipping.legal_last_name = 'Nom du représentant légal est requis';
                }
            }
            return errors;
        },
        onSubmit: async (values) => {
            // Unifier le numéro de téléphone (préfixe + numéro) avant l'envoi
            const unifiedValues = {
                ...values,
                billing: {
                    ...values.billing,
                    phone: values.billing.phonePrefix && values.billing.phone
                        ? `${values.billing.phonePrefix}${values.billing.phone}`
                        : values.billing.phone || ""
                }
            };

            // Supprimer phonePrefix du payload final
            delete unifiedValues.billing.phonePrefix;

            setPendingValues(unifiedValues);
            modalRef.current.showModal();
        },
    });

    // Custom handler for UnifiedPhoneInput - memoized to prevent infinite loops
    const handlePhoneChange = useCallback((event) => {
        const { name, value } = event.target;
        if (name === 'phonePrefix') {
            formik.setFieldValue('billing.phonePrefix', value);
        } else {
            formik.setFieldValue('billing.phone', value);
        }
    }, [formik.setFieldValue]);


    useEffect(() => {
        if (isSuccess) {
            return;
        }
        if (shippingType === 'therapist' && therapist) {
            if (therapist.addresses && therapist.addresses.length > 0) {
                formik.setFieldValue("shippingAddressId", therapist.addresses[0].id);
                updateFormAddress('shipping', therapist.addresses[0].id);
            } else {
                clearFormAddress('shipping');
            }
            // Initialiser le téléphone du thérapeute en séparant préfixe et numéro
            const therapistFullPhone = therapist.phonePrefix && therapist.phone
                ? `${therapist.phonePrefix}${therapist.phone}`
                : therapist.phone || "";

            const { phone: therapistPhone, prefix: therapistPrefix } = separatePhoneAndPrefix(
                therapistFullPhone,
                therapist.phonePrefix
            );

            formik.setFieldValue("billing.phone", therapistPhone);
            formik.setFieldValue("billing.phonePrefix", therapistPrefix);
        } else if (shippingType === 'customer' && customer) {
            if (customer.addresses && customer.addresses.length > 0) {
                formik.setFieldValue("shippingAddressId", customer.addresses[0].id);
                updateFormAddress('shipping', customer.addresses[0].id);
            } else {
                clearFormAddress('shipping');
            }
            // Initialiser le téléphone du client en séparant préfixe et numéro
            const customerFullPhone = customer.phonePrefix && customer.phone
                ? `${customer.phonePrefix}${customer.phone}`
                : customer.phone || "";

            const { phone: customerPhone, prefix: customerPrefix } = separatePhoneAndPrefix(
                customerFullPhone,
                customer.phonePrefix
            );

            formik.setFieldValue("billing.phone", customerPhone);
            formik.setFieldValue("billing.phonePrefix", customerPrefix);
        }

        // Trigger validation when shippingType changes
        formik.validateForm();
    }, [shippingType, therapist, customer, separatePhoneAndPrefix]);

    // Trigger validation when useLegalRepresentative changes
    useEffect(() => {
        formik.validateForm();
    }, [useLegalRepresentative]);

    const clearFormAddress = (type) => {
        formik.setFieldValue(`${type}.address_1`, "");
        formik.setFieldValue(`${type}.city`, "");
        formik.setFieldValue(`${type}.postcode`, "");
        formik.setFieldValue(`${type}.state`, "");
        formik.setFieldValue(`${type}.country`, "");
        formik.setFieldValue(`${type}.first_name`, shippingType === 'therapist' ? therapist?.firstname || "" : customer?.firstname || "");
        formik.setFieldValue(`${type}.last_name`, shippingType === 'therapist' ? therapist?.lastname || "" : customer?.lastname || "");
        formik.setFieldValue(`${type}.legal_first_name`, "");
        formik.setFieldValue(`${type}.legal_last_name`, "");
    };

    const updateFormAddress = (type, addressId) => {
        const currentUser = shippingType === 'therapist' ? therapist : customer;
        if (!currentUser?.addresses || currentUser.addresses.length === 0) {
            clearFormAddress(type);
            return;
        }

        const address = currentUser.addresses.find(address => address.id === parseInt(addressId));
        if (!address) {
            clearFormAddress(type);
            return;
        }

        formik.setFieldValue(`${type}.address_1`, address.street || "");
        formik.setFieldValue(`${type}.city`, address.city || "");
        formik.setFieldValue(`${type}.postcode`, address.postcode || "");
        formik.setFieldValue(`${type}.state`, address.state === null ? "" : (address.state || ""));
        formik.setFieldValue(`${type}.country`, address.isoCountry || "");
        formik.setFieldValue(`${type}.first_name`, currentUser.firstname || "");
        formik.setFieldValue(`${type}.last_name`, currentUser.lastname || "");

        // Pré-remplir les champs représentant légal si disponibles et si l'utilisateur n'a pas modifié manuellement
        if (address.legalFirstname && !formik.values[type].legal_first_name) {
            formik.setFieldValue(`${type}.legal_first_name`, address.legalFirstname);
        }
        if (address.legalLastname && !formik.values[type].legal_last_name) {
            formik.setFieldValue(`${type}.legal_last_name`, address.legalLastname);
        }
    };

    const handleConfirmOrder = async () => {
        // ✅ VÉRIFICATION SIMPLE - Thérapeute doit avoir une adresse
        if (!therapist?.addresses || therapist.addresses.length === 0) {
            toast.error("Vous devez d'abord créer une adresse dans votre profil pour passer des commandes.");
            setTimeout(() => navigate('/mon-compte/profil'), 2000); // Délai de 2 secondes
            return;
        }

        if (user.currentCredits < capsules.length) {
            setErrorMessage("Vous n'avez pas assez de crédits pour créer cette commande");
            return;
        }
        try {
            setIsCreating(true);
            setErrorMessage(null);

            // Construire le payload avec la nouvelle logique d'adresses
            const context = { user, customer, therapist, capsules };
            const payload = buildOrderPayload(pendingValues, context, shippingType, useLegalRepresentative);

            const { success, message } = await createOrder(payload);
            if (!success) {
                throw new Error(message);
            }

            toast.success(message);
            setIsSuccess(true);
        } catch (error) {
            setErrorMessage(error.message || "Une erreur est survenue lors de la création de la commande");
            setIsSuccess(false);
        } finally {
            setIsCreating(false);
        }
    };

    const handleCloseModal = () => {
        modalRef.current.close();
        setIsSuccess(false);
        setErrorMessage(null);
        if (isSuccess) {
            navigate('../');
        }
    };

    const handleRetryOrder = () => {
        setErrorMessage(null);
        handleConfirmOrder();
    };

    if (loading) return <LoadingSpinner style="medium" />;
    if (error) return <div>Error: {error.message}</div>;
    if (!capsules || !product || !customer || !therapist) return <div>Error</div>;
    if (capsules.length === 0) return <div>No order data</div>;

    if (hasNoAddress) return (
        <div className="h-full pb-8 mt-2">
            <div className="container mx-auto px-4">
                <div className="bg-base-100 rounded-xl shadow-sm p-8 text-center my-8">
                    <div className="flex flex-col items-center">
                        <div className="bg-primary/10 p-3 rounded-full mb-6">
                            <InformationCircleIcon className="h-8 w-8 text-primary" />
                        </div>
                        <h2 className="text-xl font-bold text-base-content mb-4">Aucune adresse trouvée</h2>
                        <p className="text-base-content/70 mb-6 max-w-md">
                            Vous devez d'abord créer une adresse dans votre profil pour passer des commandes.
                        </p>
                        <button
                            onClick={() => navigate('/mon-compte/informations?createAddress=true')}
                            className="btn btn-primary flex items-center gap-2"
                        >
                            <UserIcon className="h-5 w-5" />
                            Créer une adresse
                        </button>
                    </div>
                </div>
            </div>
        </div>
    )
    return (
        <div className="h-full pb-8 mt-2">
            <FormikProvider value={formik}>
                {formik.isSubmitting && <LoadingSpinner style="medium" />}
                <Form className="container mx-auto px-4">
                    <div className="mx-auto">
                        <div className="flex items-center mb-4">
                            <ShoppingCartIcon className="h-8 w-8 text-primary mr-3" />
                            <h1 className="text-3xl font-bold text-gray-800">Nouvelle Commande de Capsules</h1>
                        </div>
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-full">
                            {/* Colonne de gauche - Produit et Type d'envoi */}
                            <div className="space-y-8 h-full">
                                <div className="bg-white rounded-xl shadow-sm overflow-hidden  flex flex-col justify-start">
                                    <div className="p-6">
                                        <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                                            <Store className="h-6 w-6 mr-2 text-primary" />
                                            Produits
                                        </h2>
                                        <ProductDisplay
                                            product={product}
                                            capsules={capsules}
                                            submitButton={null}
                                        />
                                    </div>
                                    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                                        <div className="p-6">
                                            <h2 className="text-xl font-semibold text-gray-800 mb-6 flex items-center">
                                                <UsersIcon className="h-6 w-6 mr-2 text-primary" />
                                                Type d'envoi
                                            </h2>
                                            <div className="flex flex-col sm:flex-row gap-3">
                                                <button
                                                    type="button"
                                                    className={`flex-1 btn btn-md transition-all duration-200 ${shippingType === 'customer'
                                                        ? 'btn-primary shadow-xl shadow-primary/20'
                                                        : 'btn-primary btn-outline'
                                                        }`}
                                                    onClick={() => setShippingType('customer')}
                                                >
                                                    <UserGroupIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                                                    Envoi au client
                                                </button>
                                                <button
                                                    type="button"
                                                    className={`flex-1 btn btn-md transition-all duration-200 ${shippingType === 'therapist'
                                                        ? 'btn-primary shadow-xl shadow-primary/20'
                                                        : 'btn-primary btn-outline'
                                                        }`}
                                                    onClick={() => setShippingType('therapist')}
                                                >
                                                    <UserGroupIcon className="h-4 w-4 mr-2 flex-shrink-0" />
                                                    Envoi au thérapeute
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Colonne de droite - Formulaire */}
                            <div className="space-y-8 h-full overflow-visible w-fit">
                                <div className="bg-white rounded-xl shadow-sm  h-full">
                                    <div className="p-6 flex flex-row flex-wrap 2xl:flex-nowrap gap-x-12 gap-y-12 justify-between">
                                        <div className="flex gap-4 flex-col">
                                            <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                                                <UsersIcon className="h-6 w-6 mr-2 text-primary" />
                                                Informations
                                            </h2>
                                            <div className="">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-sm font-medium">Prénom</span>
                                                    <span className="text-red-500 text-sm">*</span>
                                                </div>
                                                <Field
                                                    name="shipping.first_name"
                                                    placeholder="Prénom"
                                                    className={`input input-primary min-h-[44px] ${formik.errors.shipping?.first_name && formik.touched.shipping?.first_name ? 'border-red-500' : ''}`}
                                                />
                                                {formik.errors.shipping?.first_name && formik.touched.shipping?.first_name && (
                                                    <div className="text-red-500 text-sm mt-1">{formik.errors.shipping.first_name}</div>
                                                )}
                                            </div>
                                            <div className="">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-sm font-medium">Nom</span>
                                                    <span className="text-red-500 text-sm">*</span>
                                                </div>
                                                <Field
                                                    name="shipping.last_name"
                                                    placeholder="Nom"
                                                    className={`input input-primary min-h-[44px] ${formik.errors.shipping?.last_name && formik.touched.shipping?.last_name ? 'border-red-500' : ''}`}
                                                />
                                                {formik.errors.shipping?.last_name && formik.touched.shipping?.last_name && (
                                                    <div className="text-red-500 text-sm mt-1">{formik.errors.shipping.last_name}</div>
                                                )}
                                            </div>
                                            <div className="">
                                                <div className="flex items-center gap-2 mb-1">
                                                    <span className="text-sm font-medium">Téléphone</span>
                                                    {shippingType === 'customer' && <span className="text-red-500 text-sm">*</span>}
                                                </div>
                                                <UnifiedPhoneInput
                                                    name="billing.phone"
                                                    value={formik.values.billing?.phone || ''}
                                                    phonePrefix={formik.values.billing?.phonePrefix || ''}
                                                    onChange={handlePhoneChange}
                                                    placeholder="Téléphone"
                                                    error={formik.errors.billing?.phone}
                                                    touched={formik.touched.billing?.phone}
                                                    required={shippingType === 'customer'}
                                                    size="sm"
                                                    className="w-fit"
                                                />
                                                {formik.errors.billing?.phone && formik.touched.billing?.phone && (
                                                    <div className="text-red-500 text-sm mt-1">{formik.errors.billing.phone}</div>
                                                )}
                                                {shippingType === 'customer' && !formik.errors.billing?.phone && (
                                                    <p className="text-xs text-gray-500 mt-1">Requis pour un envoi au client</p>
                                                )}
                                            </div>

                                            {/* Checkbox pour utiliser le représentant légal */}
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="checkbox"
                                                    id="useLegalRepresentative"
                                                    checked={useLegalRepresentative}
                                                    onChange={(e) => setUseLegalRepresentative(e.target.checked)}
                                                    className="checkbox checkbox-primary checkbox-sm"
                                                />
                                                <label htmlFor="useLegalRepresentative" className="text-sm font-medium cursor-pointer">
                                                    Utilisez le Représentant Légal
                                                </label>
                                            </div>

                                            {/* Champs représentant légal */}
                                            {useLegalRepresentative && (
                                                <>
                                                    <div className="">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="text-sm font-medium">Prénom (représentant légal)</span>
                                                            <span className="text-red-500 text-sm">*</span>
                                                        </div>
                                                        <Field
                                                            name="shipping.legal_first_name"
                                                            placeholder="Prénom du représentant légal"
                                                            className={`input input-primary min-h-[44px] ${formik.errors.shipping?.legal_first_name && formik.touched.shipping?.legal_first_name ? 'border-red-500' : ''}`}
                                                        />
                                                        {formik.errors.shipping?.legal_first_name && formik.touched.shipping?.legal_first_name && (
                                                            <div className="text-red-500 text-sm mt-1">{formik.errors.shipping.legal_first_name}</div>
                                                        )}
                                                    </div>
                                                    <div className="">
                                                        <div className="flex items-center gap-2 mb-1">
                                                            <span className="text-sm font-medium">Nom (représentant légal)</span>
                                                            <span className="text-red-500 text-sm">*</span>
                                                        </div>
                                                        <Field
                                                            name="shipping.legal_last_name"
                                                            placeholder="Nom du représentant légal"
                                                            className={`input input-primary min-h-[44px] ${formik.errors.shipping?.legal_last_name && formik.touched.shipping?.legal_last_name ? 'border-red-500' : ''}`}
                                                        />
                                                        {formik.errors.shipping?.legal_last_name && formik.touched.shipping?.legal_last_name && (
                                                            <div className="text-red-500 text-sm mt-1">{formik.errors.shipping.legal_last_name}</div>
                                                        )}
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                        <div className="">
                                            <h2 className="text-xl font-semibold text-gray-800 flex items-center mb-2">
                                                <MapPin className="h-6 w-6 mr-2 text-primary" />
                                                Adresse de livraison
                                            </h2>
                                            <AddressForm
                                                addresses={shippingType === 'therapist'
                                                    ? (therapist.addresses || [])
                                                    : (customer.addresses || [])}
                                                type="shipping"
                                                errors={formik.errors}
                                                touched={formik.touched}
                                                shippingType={shippingType}
                                                therapist={therapist}
                                                customer={customer}
                                                hidePersonalInfo={true}
                                                showSectionTitle={false}
                                            />
                                        </div>



                                    </div>
                                </div>


                            </div>
                        </div>

                        {/* Bouton de création en bas */}
                        <div className="mt-8 flex justify-center">
                            <button
                                type="submit"
                                disabled={formik.isSubmitting}
                                className="btn btn-primary btn-lg w-fit shadow-lg shadow-primary/20"
                            >
                                {formik.isSubmitting ? (
                                    <span className="flex items-center justify-center">
                                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                                        Création en cours...
                                    </span>
                                ) : (
                                    <span className="flex items-center justify-center">
                                        <ShoppingBagIcon className="h-5 w-5 mr-2" />
                                        Commander
                                    </span>
                                )}
                            </button>
                        </div>
                    </div>
                </Form>
            </FormikProvider>

            <Modal ref={modalRef} className="max-w-4xl max-h-[90vh] overflow-y-auto">
                {isCreating ? (
                    <div className="py-12">
                        <LoadingSpinner style="medium" />
                        <p className="text-center mt-4 text-gray-600">Création de la commande en cours...</p>
                    </div>
                ) : errorMessage ? (
                    <ErrorMessage error={errorMessage} onRetry={handleRetryOrder} />
                ) : isSuccess ? (
                    <SuccessMessage customer={customer} />
                ) : (
                    <div className="space-y-6">
                        <div className="flex items-center">
                            <CheckCircleIcon className="h-10 w-10 text-white" />
                            <h3 className="text-2xl font-bold">Confirmer la commande</h3>
                        </div>

                        <div className="space-y-4">
                            <div className="bg-base-200 p-4 rounded-lg">
                                <h4 className="font-semibold mb-2">Détails de la commande</h4>
                                <div className="space-y-2">
                                    <p><span className="font-medium">Client :</span> {customer.firstname} {customer.lastname}</p>
                                    <p><span className="font-medium">Thérapeute :</span> {therapist.firstname} {therapist.lastname}</p>
                                    <p><span className="font-medium">Crédit{user.currentCredits > 1 ? 's' : ''} capsule{user.currentCredits > 1 ? 's' : ''}:</span> {user.currentCredits}</p>
                                    <p><span className="font-medium">Capsule{capsules.length > 1 ? 's' : ''} débitée{capsules.length > 1 ? 's' : ''}:</span> {capsules.length}</p>
                                    <p><span className="font-medium">Référence des capsules :</span> #{capsules.map(capsule => capsule.id).join(', #')}</p>
                                    <p><span className="font-medium">Type d'envoi :</span> {shippingType === 'therapist' ? 'Envoi au thérapeute' : 'Envoi au client'}</p>
                                </div>
                            </div>

                            <div className="bg-base-200 p-4 rounded-lg">
                                <h4 className="font-semibold mb-2">Informations</h4>
                                <div className="space-y-1">
                                    <p><span className="font-medium">Prénom :</span> {pendingValues?.shipping.first_name}</p>
                                    <p><span className="font-medium">Nom :</span> {pendingValues?.shipping.last_name}</p>
                                    {useLegalRepresentative && (
                                        <>
                                            <p><span className="font-medium">Prénom (représentant légal) :</span> {pendingValues?.shipping.legal_first_name}</p>
                                            <p><span className="font-medium">Nom (représentant légal) :</span> {pendingValues?.shipping.legal_last_name}</p>
                                        </>
                                    )}
                                    <p><span className="font-medium">Téléphone :</span> {pendingValues?.billing?.phone || "Non renseigné"}</p>
                                </div>
                            </div>
                            <div className="bg-base-200 p-4 rounded-lg">
                                <h4 className="font-semibold mb-2">Adresse de livraison</h4>
                                <div className="space-y-1">
                                    <p><span className="font-medium">Rue :</span> {pendingValues?.shipping.address_1}</p>
                                    <p><span className="font-medium">Ville :</span> {pendingValues?.shipping.city}</p>
                                    <p><span className="font-medium">Code Postal :</span> {pendingValues?.shipping.postcode}</p>
                                    <p className="flex items-center gap-2">
                                        <span className="font-medium">Pays :</span>
                                        <span className="flex items-center gap-2">
                                            <Flag country={pendingValues?.shipping.country} className="w-5 h-5 rounded-md" />
                                            {getCountryByIso(pendingValues?.shipping.country)?.name}
                                        </span>
                                    </p>
                                    <p><span className="font-medium">Téléphone :</span> {pendingValues?.billing?.phone || "Non renseigné"}</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end gap-4 mt-8">
                            <button
                                type="button"
                                className="btn btn-ghost"
                                onClick={handleCloseModal}
                            >
                                Annuler
                            </button>
                            <button
                                type="button"
                                className={`btn btn-primary ${user.currentCredits < capsules.length ? 'hidden' : 'block'}`}
                                onClick={handleConfirmOrder}
                                disabled={formik.isSubmitting || user.currentCredits < capsules.length}
                            >
                                Confirmer la commande
                            </button>
                            {user.currentCredits < capsules.length && (
                                <button
                                    type="button"
                                    className="btn btn-primary"
                                    onClick={() => navigate('/produits')}
                                >
                                    Recharger mes crédits
                                </button>
                            )}
                        </div>
                        {user.currentCredits < capsules.length && (
                            <p className="text-red-500">Vous n'avez pas assez de crédits, veuillez recharger votre compte.</p>
                        )}
                    </div>
                )
                }
            </Modal >
        </div >
    );
};

export default CapsuleOrder;
